import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import HoldAndWinOverlaySymbolController from "./HoldAndWinOverlaySymbolController";
import { safeInit } from "../../utilities";
import { selectHoldAndWinCurrentRound } from "../../State/selectors";

/**
 * HoldAndWinOverlaySymbol - Container for spine animations in Hold & Win feature
 * Each symbol position has its own controller that manages ONE coin spine
 */
export const HoldAndWinOverlaySymbol = forwardRef(
  ({ index, reelIndex, y, symbolWidth, symbolHeight }, ref) => {
    const containerRef = useRef(null);
    const controllerRef = useRef(null);
    const [controllerReady, setControllerReady] = useState(false); // State to trigger effects when controller is ready
    const previousMultiplierRef = useRef(null); // Track previous multiplier to detect changes
    const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);

    // Initialize controller once and trigger idle animation for existing coins
    useEffect(() => {
      if (!controllerRef.current && containerRef.current) {
        safeInit(() => {
          controllerRef.current = new HoldAndWinOverlaySymbolController(
            containerRef.current,
            reelIndex,
            index,
            symbolWidth,
            symbolHeight
          );
          setControllerReady(true); // Trigger effects that depend on controller
        });
      }

      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Handle resize
    useEffect(() => {
      if (controllerRef.current) {
        controllerRef.current.resizeAnimations(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight]);

    // Watch for multiplier changes and trigger display (updates during spins)
    useEffect(() => {
      if (!controllerRef.current) {
        return;
      }
      if (!holdAndWinCurrentRound?.storage?.coin_multipliers) {
        return;
      }
      if (!holdAndWinCurrentRound?.reels) {
        return;
      }

      // Get current multiplier value for this position
      const multiplierValue = holdAndWinCurrentRound.storage.coin_multipliers?.[reelIndex]?.[index];

      // Check if multiplier actually changed
      const multiplierChanged = previousMultiplierRef.current !== multiplierValue;

      if (!multiplierChanged) {
        return;
      }

      // Update ref with current value
      previousMultiplierRef.current = multiplierValue;

      // Show multiplier if value is 2, 3, or 10
      if (multiplierValue && [2, 3, 10].includes(Number(multiplierValue))) {
        controllerRef.current.showMultiplier(Number(multiplierValue));
      } else {
        // Hide multiplier if value is 1 or undefined
        controllerRef.current.hideMultiplier();
      }
    }, [holdAndWinCurrentRound, reelIndex, index, controllerReady]);

    // Expose controller methods
    useImperativeHandle(ref, () => ({
      getController: () => controllerRef.current,
      showLandingAnimation: (symbolId) => {
        controllerRef.current?.showLandingAnimation(symbolId);
      },
      showWinAnimation: (symbolId, loop = true, showText = true) => {
        controllerRef.current?.showWinAnimation(symbolId, loop, showText);
      },
      showDarkAnimation: (symbolId) => {
        controllerRef.current?.showDarkAnimation(symbolId);
      },
      hideAnimation: () => {
        controllerRef.current?.hideAnimation();
      },
      fadeCoinValueText: () => {
        controllerRef.current?.fadeCoinValueText();
      },
      applyMultiplierAndAnimate: (multiplierValue) => {
        return controllerRef.current?.applyMultiplierAndAnimate(
          multiplierValue
        );
      },
    }));

    return (
      <Container
        ref={containerRef}
        y={y}
        x={0}
        zIndex={7}
        sortableChildren
        name={`hold_and_win_overlay_symbol_${reelIndex}_${index}`}
      />
    );
  }
);

export default HoldAndWinOverlaySymbol;
